import {RunConfigService, NsMissingCredentialsError, UrlService} from '@nuskin/ns-util';
import {UserService} from '@nuskin/ns-account';
import ShopStorage from '../shopStorage';
import PaymentDto from './paymentDto';
import PaymentTypeContextDto from './paymentTypeContextDto';
import InstallmentPricingDto from './installmentPricingDto';
import PaymentType from './paymentType.js';

import axios from 'axios';
import { getCachedConfigField } from '@nuskin/configuration-sdk';

// Service URI Paths
const PMNT_BASE = '/shop-service/api/v2/payment';
const PMNT_PATH_AWS_V1 = '/paymentprofile/v1';
const PMNT_PATH_AWS_V2 = '/paymentprofile/v2';
const EXT_PMNT_PATH_LEGACY = '/external-payment/api/v1';

const PMNT_TYPES_PATH = PMNT_BASE + '/typesallowed';

// Response Body Keys
const PAYMENT_LIST = 'PaymentList';
const PAYMENT = 'Payment';
const PAYMENT_TYPE_CONTEXT_LIST = 'PmtTypeCntxtList';
const INSTALLMENTS = 'installments';
const INSTALLMENT_PRICING_STRING = 'String';

const getLegacyBaseURL = () => UrlService.getSiteUrl();

const getAWSBaseURL = () => UrlService.getAwsUrl();

const getPmntURLV1 = () => getAWSBaseURL() + PMNT_PATH_AWS_V1;

const getPmntURLV2 = () => getAWSBaseURL() + PMNT_PATH_AWS_V2;

const getPmntTypeURL = () => getLegacyBaseURL() + PMNT_TYPES_PATH;

const getExtPmntURL = () => getLegacyBaseURL() + EXT_PMNT_PATH_LEGACY;

const getHeaders = () => {
    const runConfig = RunConfigService.getRunConfig();
    return {
        client_id: runConfig.clientId,
        client_secret: runConfig.clientSecret,
        Accept: "application/json"
    }
};

const getHeadersWithAuth = () => ({
    ...getHeaders(),
    Authorization: `JWT ${getUser().eid}`
});

let user;
let country;
let language;

/**
 * Provides payment- and installment-related functions, and provides interfaces with the payment-related endpoints of shop-service
 * @namespace PaymentService
 */
const PaymentService = {
    getSavedPayments,
    getSavedPaymentsInCurrentMarket,
    addPayment,
    editSavedPayment,
    deleteSavedPayment,
    getAllowedPaymentTypes,
    getAvailableInstallmentCounts,
    getInstallmentPricing,
    getInstallmentsForPaymentType,
    getPricingForInstallmentCount,
    tokenizePaymentNumber
};
export default PaymentService;



//=================================================================//
//
//  PUBLIC FUNCTIONS
//
//=================================================================//

/**
 * Returns a list of the user's saved payments in all markets
 * @param {boolean} [includeAll] (Optional) if `true`, includes all payments
 * saved by user. This includes wire and direct debit payments, if applicable.
 * @returns {Promise.<Array.<PaymentDto>>} Promise that resolves to an array of payments
 * @memberof PaymentService
 * @public
 * @see PaymentDto
 */
async function getSavedPayments(includeAll = false) {
    const params = { locale: getLocale() };
    const baseURL = includeAll ? getPmntURLV2() : getPmntURLV1();
    const url = `${baseURL}/${getUser().id}?${UrlService.toQueryString(params)}`;

    let response = await fetch(url, {
        method: 'GET',
        headers: getHeadersWithAuth(),
        credentials: 'omit'
    });

    let body = await transformFetchResponse(response);

    if (PAYMENT_LIST in body && body[PAYMENT_LIST] instanceof Array) {
        return body[PAYMENT_LIST].map(el => new PaymentDto(el));
    }

    throw new Error('PaymentService error: payment list response is missing or malformed');
}

/**
 * Returns a list of the user's saved payments in current market
 * @param {{shopCntxt?: string, orderType?: string, isSignup?: boolean, cntryCode?: string, shipToCntryCode?: string}}
 *          [parameters] - (optional) Additional parameters:
 * @param {string} [parameters.shopCntxt] - (optional) Shop context for this request.
 * @param {string} [parameters.orderType] - (optional) Order type: "SINGLE ORDER", "ADR CREATE", "ADR OVERRIDE",
 *          "ADR MANAGE", "SHIP IMMEDIATE", or "PROFILE".
 * @param {boolean} [parameters.isSignup = false] - (optional) Flag indicating whether this request is made in a Signup context.
 * @param {string} [parameters.cntryCode = current country] - (optional) Country code; defaults to current country.
 * @param {string} [parameters.shipToCntryCode] - (optional) Country code to ship to.
 * @returns {Promise.<PaymentDto[]>} Promise that resolves to a list of payments.
 * @memberof PaymentService
 * @public
 * @see PaymentDto
 */
async function getSavedPaymentsInCurrentMarket(parameters = {}) {
    const [types, payments] = await Promise.all([
        getAllowedPaymentTypes(parameters),
        getSavedPayments()
    ]);

    const pmntTypeIDs = types.map(t => t.id);
    return payments.filter(pmnt => pmntTypeIDs.includes(pmnt.paymentTypeId));
}

/**
 * Saves the given {@link PaymentDto|payment} as a new payment
 * @param {PaymentDto} payment - new payment to save
 * @param {*} [options] - Additional options
 * @param {boolean} [options.tokenize = false] - (optional) Tokenize the payment number before saving
 * @returns {Promise.<PaymentDto>} Promise that resolves to the saved payment
 * @memberof PaymentService
 * @public
 * @see PaymentDto
 */
async function addPayment(payment, options) {
    if (!payment) throw new Error('Null argument error');
    if (!(payment instanceof PaymentDto)) throw new Error(`Illegal argument error: expected PaymentDto, got ${payment.constructor.name}`);

    const DEFAULT_OPTIONS = {
        tokenize: false
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);

    if (options.tokenize && payment.paymentNumber) {
        payment.paymentNumber = await tokenizePaymentNumber(payment.paymentNumber);
    }

    const params = { locale: getLocale() };

    let url = `${getPmntURLV1()}/${getUser().id}?${UrlService.toQueryString(params)}`;

    let response = await fetch(url, {
        method: 'POST',
        headers: { ...getHeadersWithAuth(), 'content-type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(payment)
    });

    let body = await transformFetchResponse(response);

    if (PAYMENT in body) {
        return new PaymentDto(body[PAYMENT]);
    }

    throw new Error('PaymentService error: payment response is missing or malformed');
}

/**
 * Saves edits made to the given payment. More specifically, the saved payment matching the ID of the given payment is
 *  overwritten with the given payment.
 * @param {PaymentDto} payment
 * @returns {Promise.<PaymentDto>} Promise that resolves to the updated payment
 * @memberof PaymentService
 * @public
 * @see PaymentDto
 */
async function editSavedPayment(payment) {
    if (!payment) throw new Error('Null argument error');
    if (!(payment instanceof PaymentDto)) throw new Error(`Illegal argument error: expected PaymentDto, got ${payment.constructor.name}`);

    const params = { locale: getLocale() };

    let url = `${getPmntURLV1()}/${getUser().id}?${UrlService.toQueryString(params)}`;
    let headers = { ...getHeadersWithAuth(), 'content-type': 'application/json' };

    let response = await fetch(url, {
        method: 'PUT',
        headers: headers,
        credentials: 'omit',
        body: JSON.stringify(payment)
    });

    let body = await transformFetchResponse(response);

    if (PAYMENT in body) {
        return new PaymentDto(body[PAYMENT]);
    }

    throw new Error('PaymentService error: payment response is missing or malformed');
}

/**
 * Deletes the payment matching the given ID
 * @param {number} bePaymentTypeId - ID of the payment to be deleted
 * @returns {Promise.<boolean>} Promise that resolves to true if deletion was successful, false if unsuccessful
 * @memberof PaymentService
 * @public
 * @see PaymentDto.bePaymentTypeId
 */
async function deleteSavedPayment(bePaymentTypeId) {
    if (!bePaymentTypeId) throw new Error('Null argument error');

    const params = { locale: getLocale() };

    let url = `${getPmntURLV1()}/${bePaymentTypeId}?${UrlService.toQueryString(params)}`;

    let resp = await fetch(url, {
        method: 'DELETE',
        headers: getHeadersWithAuth(),
        credentials: 'omit'
    }).then(function (response) {
        if (response.status !== 200) {
            throw new Error('PaymentService error:'+ response.status);
        }
        else {
            return response.ok;
        }
    });

    return resp;
}

/**
 * Returns a list of allowed payment types for the given market and contexts
 * @param {object} [parameters] - optional query parameters:
 * @param {string} [parameters.shopCntxt] - shop context
 * @param {string} [parameters.orderType] - "SINGLE ORDER", "ADR CREATE", "ADR OVERRIDE", "ADR MANAGE", "SHIP IMMEDIATE", or "PROFILE"
 * @param {boolean} [parameters.isSignup = false] - flag indicating whether ordering in Signup context
 * @param {string} [parameters.cntryCode = current country] - country code; defaults to current country
 * @param {string} [parameters.shipToCntryCode] - country code to ship to
 * @param {string} [parameters.locale = current locale] - locale; defaults to current locale
 * @returns {Promise.<Array.<PaymentTypeContextDto>>} Promise that resolves to list of payment types
 * @memberof PaymentService
 * @public
 * @see PaymentTypeContextDto
 */
async function getAllowedPaymentTypes(parameters) {
    const DEFAULT_PARAMS = {
        cntryCode: getCountry(),
        locale: getLocale()
    };
    parameters = Object.assign({}, DEFAULT_PARAMS, parameters);

    let url = `${getPmntTypeURL()}?${UrlService.toQueryString(parameters)}`;

    let response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'omit'
    });

    let body = await transformFetchResponse(response);

    if (PAYMENT_TYPE_CONTEXT_LIST in body && body[PAYMENT_TYPE_CONTEXT_LIST] instanceof Array) {
        return body[PAYMENT_TYPE_CONTEXT_LIST].map(el => new PaymentTypeContextDto(el));
    }

    throw new Error('PaymentService error: payment type context list response is missing or malformed');
}

/**
 * Returns a list of the available installment counts for a country
 * @param {object} [options] - options:
 * @param {string} [options.country] - country code; defaults to the current country
 * @returns {Promise.<number[]>} Promise that resolves to array of installment counts
 * @memberof PaymentService
 * @public
 */
async function getAvailableInstallmentCounts(options) {
    const DEFAULT_OPTIONS = {
        country: getCountry()
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);
    const params = { countryCode: options.country };

    let url = `${getLegacyBaseURL()}${PMNT_BASE}/installments?${UrlService.toQueryString(params)}`;

    let response = await fetch(url, {
        method: 'GET',
        headers: getHeadersWithAuth(),
        credentials: 'omit'
    });

    let body = await transformFetchResponse(response);

    if (INSTALLMENTS in body && body[INSTALLMENTS] instanceof Array) {
        return body[INSTALLMENTS];
    }

    throw new Error('PaymentService error: installments response is missing or malformed');
}

/**
 * Returns installment pricing data representing available installments for all payment types, for the given order total
 * @param {number} amount - grand total price of order
 * @returns {Promise.<InstallmentPricingDto>} Promise that resolves to installment pricing data
 * @memberof PaymentService
 * @public
 * @see InstallmentPricing
 */
async function getInstallmentPricing(amount) {
    if (typeof amount !== 'number') throw new Error(`Illegal argument error: expected number, got ${typeof amount}`);
    if (Number.isNaN(amount)) throw new Error('Illegal argument error: expected number, got NaN');

    let ipData = ShopStorage.getInstallmentPricing();
    let installmentPricing = (ipData instanceof InstallmentPricingDto) ? ipData : new InstallmentPricingDto(ipData);

    if (installmentPricing.amount.value === amount) {
        return installmentPricing;
    }

    const params = {
        accountId: getCachedConfigField('payUMerchantID'),
        amount
    }
    let url = `${getExtPmntURL()}/installmentPricing?${UrlService.toQueryString(params)}`;

    let response = await fetch(url, {
        method: 'GET',
        headers: getHeadersWithAuth(),
        credentials: 'omit'
    });

    let body = await transformFetchResponse(response);

    if (INSTALLMENT_PRICING_STRING in body) {
        let data = JSON.parse(body[INSTALLMENT_PRICING_STRING]);
        installmentPricing = new InstallmentPricingDto(data);
        ShopStorage.setInstallmentPricing(installmentPricing);
        return installmentPricing;
    }

    throw new Error('PaymentService error: installment pricing response is missing or malformed');
}

/**
 * Returns a list of installment counts available for the given payment type and order total
 * @param {number} amount - grand total price of order
 * @param {number} paymentTypeId - payment type ID
 * @returns {Promise.<Array.<number>>} Promise that resolves to array of installment counts
 * @memberof PaymentService
 * @public
 * @see PaymentDto.paymentTypeId
 */
async function getInstallmentsForPaymentType(amount, paymentTypeId) {
    let installmentPricing = await getInstallmentPricing(amount);

    let methodFee = installmentPricing.paymentMethodFee
        .find(f => f.paymentMethod === PaymentType.zooz[paymentTypeId]);

    if (!methodFee) return [];

    return methodFee.pricingFees
        .map(p => Number.parseInt(p.installments))
        .filter(i => i > 1);
}

/**
 * Retrieves installment pricing data for the given installment count, payment type, and order total.
 * Returns a promise that resolves to installment pricing data; resolves to null if no data can be found matching the given parameters.
 * @param {number} amount - grand total price of order
 * @param {number} paymentTypeId - payment type ID of the payment method used
 * @param {number} installmentNumber - number of installments
 * @returns {Promise.<PricingData>} Promise that resolves to installment pricing data
 * @memberof PaymentService
 * @public
 * @see PaymentDto.paymentTypeId
 * @see PricingData
 */
async function getPricingForInstallmentCount(amount, paymentTypeId, installmentNumber) {
    let installmentPricing = await getInstallmentPricing(amount);

    let methodFee = installmentPricing.paymentMethodFee
        .find(f => f.paymentMethod === PaymentType.zooz[paymentTypeId]);

    if (!methodFee) return null;

    let pricingFee = methodFee.pricingFees
        .find(p => Number.parseInt(p.installments) === installmentNumber);

    if (!pricingFee) return null;

    return pricingFee.pricing;
}

/**
 * Tokenizes the given payment number via Paymetric. Returns the payment token.
 * @param {string} paymentNumber - Raw payment number to tokenize.
 * @returns {Promise<string>} Promise that resolves to the tokenized payment number.
 * @memberof PaymentService
 * @public
 */
async function tokenizePaymentNumber(paymentNumber) {
    const accessToken = await getPaymetricAccessToken();
    const PAYMENT_KEY = 'PaymentNumber'
    const data = new Map();
    data.set(PAYMENT_KEY, paymentNumber);
    await tokenizeData(data, accessToken);
    const results = await getTokenizedData(accessToken);
    return results.get(PAYMENT_KEY);
}


//=================================================================//
//
//  PRIVATE FUNCTIONS
//
//=================================================================//

/**
 * Generates a new access token for the PayMetric XI Intercept API.
 * @returns {Promise<string>} Promise that resolves to PayMetric access token.
 * @memberof PaymentService
 * @private
 */
async function getPaymetricAccessToken() {
    const url = `${getAWSBaseURL()}/tokenize/v1/access-token`;
    const response = await fetch(url, {
        method: 'GET',
        credentials: 'omit'
    });
    const body = await transformFetchResponse(response);
    return body['accessToken'];
}

/**
 * Submits a collection of data to Paymetric for tokenization.
 * @param {Map<string, string>} data - Data to tokenize, organized as key-value pairs.
 * @param {string} accessToken - Paymetric access token.
 * @returns {Promise<void>} Promise that resolves if tokenization request succeeds.
 * @memberof PaymentService
 * @private
 */
async function tokenizeData(data, accessToken) {
    const url = getCachedConfigField('paymetricTokenizationURL');
    const merchantGuid = getCachedConfigField('paymetricMerchantGuid');

    if (!url) throw new Error('Paymetric tokenization URL missing from market configuration');
    if (!merchantGuid) throw new Error('Paymetric merchant GUID missing from market configuration');

    const requestBody = {
        MerchantGuid: merchantGuid,
        AccessToken: accessToken,
        FormFields: Array.from(data).map(([key, value]) => ({
            Name: key,
            Value: value,
            IsToTokenize: true
        }))
    };

    return axios.post(url, requestBody, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        transformRequest: body => jsonToFormUrlEncoded(body)
    })
        .then(({data}) => {
        /*
        Responses from Paymetric's XI Intercept API include a HasPassed property, which is true if the
        request was valid and successfully processed, and false otherwise.
        */
            if (!data.HasPassed) throw new Error(data.Message);
        });
}

/**
 * Retrieves all PayMetric tokens that were generated with the given access token.
 * @param {string} accessToken - PayMetric access token.
 * @returns {Promise<Map<string,string>>} Promise that resolves to collection of Paymetric tokens.
 * @memberof PaymentService
 * @private
 */
async function getTokenizedData(accessToken) {
    const url = `${getAWSBaseURL()}/tokenize/v1/token/${accessToken}`;
    const response = await fetch(url, {
        method: 'GET',
        credentials: 'omit'
    });
    const body = await transformFetchResponse(response);

    const result = new Map();
    Object.entries(body['tokens'] || {})
        .forEach(([key, value]) => result.set(key, value));
    return result;
}

/**
 * Converts a JSON object to a string in x-www-form-urlencoded format.
 * @param {any} element - Entity to URI encode. This may be an object or primitive value.
 * @param {string} [key] - (optional) Entity key.
 * @param {any[]} [list] - (optional) List of already encoded entities.
 * @returns {string} Encoded string
 * @memberof PaymentService
 * @private
 */
function jsonToFormUrlEncoded(element, key, list = []) {
    if (typeof element === 'object') {
        for (let idx in element) {
            jsonToFormUrlEncoded(element[idx], key ? `${key}[${idx}]` : idx, list);
        }
    } else if (typeof element !== 'function') {
        list.push(`${key}=${encodeURIComponent(element)}`);
    }
    return list.join('&');
}

/**
 * Returns the body of a Fetch API response object
 * @param {*} response - fetch response
 * @returns {Promise.<*>}
 * @throws {object} throws server response body as error if server returned an error
 * @memberof PaymentService
 * @private
 */
async function transformFetchResponse(response) {
    let body = await response.json();
    if (response.ok) return body;

    throw body;
}

/**
 * Returns the currently authenticated user
 * @returns {object} User
 * @throws {NsMissingCredentialsError} if user is not logged in
 * @memberof PaymentService
 * @private
 */
function getUser() {
    user = user || UserService.getUser();
    if (user) return user;

    throw new NsMissingCredentialsError();
}

/**
 * Returns the country code of the current country
 * @returns {string} country code
 * @memberof PaymentService
 * @private
 */
function getCountry() {
    country = country || RunConfigService.getRunConfig().country;
    return country;
}

/**
 * Returns the language code of the current language
 * @returns {string} language code
 * @memberof PaymentService
 * @private
 */
function getLanguage() {
    language = language || RunConfigService.getRunConfig().language;
    return language;
}

/**
 * Returns the locale of the current market
 * @returns {string}
 * @memberof PaymentService
 * @private
 */
function getLocale() {
    return `${getLanguage()}_${getCountry()}`;
}
